/**
 * Reducer specifically for any sort of crossborder transactions.
 * Mostly used by the embedded p2p cross border calculator.
 */

import { ActionTypes as types } from '../constants'
import serverProps from '../utils/serverProps'

function getInitialState(props = serverProps) {
  const {
    recipientData: {
      recipient = '',
      isCrossBorderRecipient = false,
      amountFormatted = '',
      recipientCountryCode: toCountryCode = '',
      recipientCountryName: toCountryName = '',
      senderCountryCode: fromCountryCode = '',
      rate = '',
      recipientCurrencyCode: toCurrencyCode = '',
      senderLocalCurrency: fromCurrencyCode = '',
      recipientLocalCurrency: toCountryLocalCurrency = '',
      availableCurrencies: fromCurrencyCodeList = [],
      recipientCurrencies: toCurrencyCodeList = [],
      recipientCurrencyMap: toCurrencySymbolMap = {},
    } = {},
    currencyFormat: { separator = ',', decimal = '.' } = {},
    disableFeeDisclaimerMessage: disableFeeDisclaimerMessage = false,
  } = props

  // set initialState. This obj becomes immutable, since that's what Redux expects.
  return {
    disclaimerCountries: ['AU'], // TODO: put in server config
    disableFeeDisclaimerMessage,
    groupSeparator: separator,
    decimalSeparator: decimal,
    showXBCalculator: !!isCrossBorderRecipient,

    rate,
    feeAmountBank: '0.00',
    totalCostBank: '0.00',
    feeAmountCard: '0.00',
    totalCostCard: '0.00',
    toCurrencyCode,
    fromCurrencyCode,
    toCountryLocalCurrency,
    fromCurrencyCodeList,
    toCurrencyCodeList,
    toCurrencySymbolMap,

    originAmount: '',
    destinationAmount: '',
    toCountryCode,
    toCountryName,
    fromCountryCode,

    isLoadingDestinationAmount: false,
    isLoadingExchangeRate: false,
    isLoadingFees: false,
    latestAmountFetchTime: null,
    latestExchangeRateFetchTime: null,

    recipient,
    displayAmount: amountFormatted,

    // fee mismatch
    xbCountryMismatch: false,
    xbActualRecipientCountry: '',
    xbChosenCountry: '',
    fxQuoteId: '',
  }
}

const initialState = getInitialState()

export default function crossBorder(state = initialState, action) {
  switch (action.type) {
    case types.TRANSACTION.RECEIVED_FUNDING_OPTIONS:
      return {
        ...state,
        xbCountryMismatch: action.data.xbCountryMismatch,
        xbActualRecipientCountry: action.data.xbActualRecipientCountry,
        xbChosenCountry: action.data.xbChosenCountry,
      }

    case types.CROSS_BORDER.SET_INITIAL_STATE:
      return {
        ...state,
        ...action.data,
      }

    case types.CROSS_BORDER.CHANGE_ORIGIN_COUNTRY:
      return {
        ...state, // ES7 obj spread. Same as Object.assign({}, state, fromCountry, fromCurrencyCode)
        fromCountryCode: action.data.fromCountryCode,
        fromCurrencyCode: action.data.fromCurrencyCode,
      }

    case types.CROSS_BORDER.CHANGE_ORIGIN_CURRENCY_CODE:
      return {
        ...state,
        fromCurrencyCode: action.data.currencyCode,
      }

    case types.CROSS_BORDER.CHANGE_DESTINATION_CURRENCY_CODE:
      return {
        ...state,
        toCurrencyCode: action.data.currencyCode,
      }

    case types.CROSS_BORDER.CHANGE_RECIPIENT:
      return {
        ...state,
        recipient: action.data.recipient,
        isRecipientValid: action.data.isRecipientValid,
      }

    case types.CROSS_BORDER.CHANGE_DESTINATION_COUNTRY:
      return {
        ...state,
        toCountryLocalCurrency: action.data.localCurrency,
        toCountryCode: action.data.countryCode,
        toCurrencyCode: action.data.currencyCode,
        toCountryName: action.data.name,
      }

    case types.CROSS_BORDER.ORIGIN_AMOUNT_LOADED:
      return {
        ...state,
        rate: action.data.rate,
        originAmount: action.data.fromAmountPrecise,
      }

    case types.CROSS_BORDER.RECEIVED_DESTINATION_AMOUNT:
      return {
        ...state,
        rate: action.data.rate,
        destinationAmount: action.data.toAmountPrecise,
        displayAmount: action.data.toAmount, // used on the recipient page
        isLoadingDestinationAmount: false,
        isLoadingExchangeRate: false,
      }

    case types.CROSS_BORDER.RECEIVED_ORIGIN_AMOUNT:
      return {
        ...state,
        rate: action.data.rate,
        originAmount: action.data.fromAmountPrecise,
        displayAmount: action.data.toAmount, // used on the recipient page
        isLoadingDestinationAmount: false,
        isLoadingExchangeRate: false,
      }
    case types.CROSS_BORDER.RECEIVED_EXCHANGE_RATE:
      return {
        ...state,
        rate: action.data.rate,
        // intentionally ignore everything else because
        // we used fake data to ensure we'd get a good
        // exchange rate (sending 0 would result in a
        // bad call)
        isLoadingExchangeRate: false,
        isLoadingDestinationAmount: false,
        isLoadingFees: false,
      }
    case types.CROSS_BORDER.FETCH_CONVERSION_FAILED:
      // in case of error, set amount to 0
      return {
        ...state,
        originAmount: '0',
        destinationAmount: '0',
        isLoadingExchangeRate: false,
        isLoadingDestinationAmount: false,
        isLoadingFees: false,
      }

    case types.CROSS_BORDER.RECEIVED_FEE:
      return {
        ...state,
        feeAmountBank: action.data.bank.fee,
        totalCostBank: action.data.bank.totalCost,
        feeAmountCard: action.data.card.fee,
        totalCostCard: action.data.card.totalCost,
        isLoadingFees: false,
        fxQuoteId: action.data.fxQuoteId,
      }

    case types.CROSS_BORDER.RECEIVED_FX_QUOTE_ID:
      return {
        ...state,
        fxQuoteId: action.data.fxQuoteId,
      }

    case types.CROSS_BORDER.CHANGE_ORIGIN_AMOUNT:
      return {
        ...state,
        originAmount: action.data.amountPrecise,
        destinationAmount: action.data.amountIsZero
          ? '0'
          : state.destinationAmount,
        isLoadingDestinationAmount:
          state.toCurrencyCode !== state.fromCurrencyCode,
        isInDebounceWindow:
          state.toCurrencyCode !== state.fromCurrencyCode &&
          action.data.isInDebounceWindow,
      }

    case types.CROSS_BORDER.CHANGE_DESTINATION_AMOUNT:
      return {
        ...state,
        destinationAmount: action.data.amountPrecise,
        originAmount: action.data.amountIsZero ? '0' : state.originAmount,
        isLoadingDestinationAmount:
          state.toCurrencyCode !== state.fromCurrencyCode,
        isInDebounceWindow:
          state.toCurrencyCode !== state.fromCurrencyCode &&
          action.data.isInDebounceWindow,
      }
    case types.CROSS_BORDER.REQUEST_ORIGIN_AMOUNT:
      return {
        ...state,
        isInDebounceWindow: false,
        isLoadingDestinationAmount: true,
        isLoadingFees: true,
        isLoadingExchangeRate: action.data.currencyChanged,
        latestAmountFetchTime: action.data.latestAmountFetchTime,
      }
    case types.CROSS_BORDER.REQUEST_DESTINATION_AMOUNT:
      return {
        ...state,
        isInDebounceWindow: false,
        isLoadingDestinationAmount: true,
        isLoadingFees: true,
        isLoadingExchangeRate: action.data.currencyChanged,
        latestAmountFetchTime: action.data.latestAmountFetchTime,
      }
    case types.CROSS_BORDER.REQUEST_EXCHANGE_RATE:
      return {
        ...state,
        isLoadingDestinationAmount: true,
        isLoadingFees: true,
        isLoadingExchangeRate: true,
        latestExchangeRateFetchTime: action.data.latestExchangeRateFetchTime,
      }

    case types.TRANSACTION.SUBMITTING_RECIPIENT:
    case types.TRANSACTION.LEAVING_SUCCESS_PAGE:
    case types.TRANSACTION.CANCELLED_TRANSACTION:
      return initialState

    case types.EXTERNAL.RECEIVED_EXTERNAL_PARAMS:
      const {
        fromCountryCode,
        toCountry: toCountryCode,
        fromCurrencyCode,
        currencyCode: toCurrencyCode,
        recipient,
        amount: destinationAmount,
        originAmount,
      } = action.data
      // turn string value in URL to boolean
      const showRecipientFirst = action.data.showRecipientFirst === 'true'

      return {
        ...state,
        originAmount,
        fromCountryCode,
        toCountryCode,
        fromCurrencyCode,
        toCurrencyCode,
        recipient,
        destinationAmount,
        showRecipientFirst,
      }

    default:
      return state
  }
}
